/**
 * Fábrica central de llaves de TanStack Query. Úsela siempre en lugar de
 * arreglos sueltos: así `invalidateQueries({ queryKey: queryKeys.loans.all })`
 * alcanza todas las variantes (listas, detalle) de una entidad.
 *
 * Convención: `all` es la raíz de la entidad; `list(params)` y
 * `detail(id)` cuelgan de ella.
 */

type Params = Record<string, unknown> | undefined

/** Crea las llaves estándar (all / list / detail) de una entidad. */
function entityKeys<const Name extends string>(name: Name) {
  return {
    all: [name] as const,
    list: (params?: Params) => [name, 'list', params ?? {}] as const,
    detail: (id: string) => [name, 'detail', id] as const,
  }
}

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: ['auth', 'me'] as const,
    sessions: ['auth', 'sessions'] as const,
    /** Estado de la verificación en dos pasos del usuario. */
    twoFactor: ['auth', 'two-factor'] as const,
  },
  publicBranding: ['public', 'branding'] as const,
  simulation: (input: Params) => ['public', 'simulation', input ?? {}] as const,
  settings: ['settings'] as const,
  /** Datos guardados en el teléfono para trabajar sin señal. */
  offline: {
    /** Paquete de campo del usuario (IndexedDB). */
    fieldPack: (userId: string) => ['offline', 'field-pack', userId] as const,
  },
  dashboard: (params?: Params) => ['dashboard', params ?? {}] as const,
  notifications: ['notifications'] as const,
  branches: entityKeys('branches'),
  products: entityKeys('products'),
  employees: entityKeys('employees'),
  auditLogs: entityKeys('audit-logs'),
  clients: entityKeys('clients'),
  loans: entityKeys('loans'),
  payments: entityKeys('payments'),
  cash: entityKeys('cash'),
  collections: entityKeys('collections'),
  compliance: entityKeys('compliance'),
  reports: entityKeys('reports'),
  documents: entityKeys('documents'),
  /** Facturas electrónicas (FEL). */
  invoices: entityKeys('invoices'),
  /** Contabilidad: la raíz invalida diario, balanza, mayor, conciliación y períodos. */
  accounting: entityKeys('accounting'),
  /** Libro diario (raíz para `useServerTable`). */
  accountingJournal: ['accounting', 'journal'] as const,
  /** Libro mayor de una cuenta (raíz para `useServerTable`). */
  accountingLedger: ['accounting', 'ledger'] as const,
  /** Balanza de comprobación con sus filtros. */
  trialBalance: (params?: Params) => ['accounting', 'trial-balance', params ?? {}] as const,
  /** Conciliación de la cartera contable con la de préstamos. */
  accountingReconciliation: (params?: Params) => ['accounting', 'reconciliation', params ?? {}] as const,
  /** Períodos contables. */
  accountingPeriods: ['accounting', 'periods'] as const,
  /** Catálogo de cuentas y política contable. */
  accountingSettings: ['accounting', 'settings'] as const,
  /** Cotización de un pago (cuelga de `payments` para invalidarse con los pagos). */
  paymentQuote: (loanId: string, params?: Params) => ['payments', 'quote', loanId, params ?? {}] as const,
  /** Caja abierta del usuario (cuelga de `cash`). */
  cashCurrent: ['cash', 'current'] as const,
  /** Cola de mora (raíz para `useServerTable`). */
  collectionQueue: ['collections', 'queue'] as const,
  /** Conteo de la cola por tramo de atraso. */
  collectionBucketCounts: (params?: Params) => ['collections', 'bucket-counts', params ?? {}] as const,
  /** Bandeja de promesas de pago (raíz para `useServerTable`). */
  collectionPromises: ['collections', 'promises'] as const,
  /** Resumen de alertas abiertas de cumplimiento. */
  complianceSummary: ['compliance', 'summary'] as const,
  /** Alertas de cumplimiento (raíz para `useServerTable`). */
  complianceAlerts: ['compliance', 'alerts'] as const,
  /** Registro de transacciones en efectivo (RTE). */
  complianceCashRegister: (params?: Params) => ['compliance', 'cash-register', params ?? {}] as const,
  /** Clientes PEP (raíz para `useServerTable`). */
  compliancePepClients: ['compliance', 'pep-clients'] as const,
  /** Datos de un reporte con sus filtros. */
  report: (reportId: string, params?: Params) => ['reports', reportId, params ?? {}] as const,
  /** Verificación de DPI duplicado mientras se registra un cliente. */
  clientLookup: (cui: string) => ['clients', 'lookup', cui] as const,
  /** Score interno recalculado de un cliente (cuelga del detalle para invalidarse con él). */
  clientScore: (clientId: string) => ['clients', 'detail', clientId, 'score'] as const,
  /** Tablero Kanban de originación (con el filtro de agencia). */
  loanPipeline: (params?: Params) => ['loans', 'pipeline', params ?? {}] as const,
  /** Expediente de evaluación: garantías, fiadores, estudio y verificación. */
  loanUnderwriting: (loanId: string) => ['loans', 'detail', loanId, 'underwriting'] as const,
  /** Simulación de una solicitud con las condiciones del formulario. */
  loanSimulation: (input: Params) => ['loans', 'simulation', input ?? {}] as const,
  /** Gestiones de cobro registradas para un préstamo. */
  loanCollectionActions: (loanId: string) => ['collections', 'loan', loanId, 'actions'] as const,
} as const
